/* A COACH'S EQUIPMENT ICON, MADE IN HIS BROWSER (W62 step C(d), 2026-09-23).
 *
 * He picks a PNG, JPG, WebP or SVG up to 2 MB; the browser draws it, fitted and centred, into a 96×96
 * square and sends ONLY that PNG. The bucket takes `image/png` and nothing else (256 KB), so this is
 * the only shape an icon can ever have on the server.
 *
 * ⚠️ AN SVG IS NEVER STORED — it can carry script. It is drawn here through an `<img>`, and an image
 * element renders an SVG as a picture: its scripts do not run and it cannot fetch anything. What
 * leaves the machine is pixels.
 *
 * 🔴 TRANSPARENT OR NOT DECIDES HOW IT IS DRAWN. An icon with a transparent background is treated as a
 * SHAPE: its colour is dropped (every pixel inked black, the alpha kept) and it is stored as
 * `…-mask.png`, which the phone and the web draw in the current ink — so his icon sits in the set
 * like ours do, in light and dark. Anything opaque is a PICTURE and is drawn as itself. */

export const ICON_SIZE = 96;
export const ICON_MAX_BYTES = 2 * 1024 * 1024;
export const ICON_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"] as const;

/** The refusal for a file this cannot take, or null. Said in words a coach can act on. */
export function iconRefusal(file: { type: string; size: number }): string | null {
  if (!(ICON_TYPES as readonly string[]).includes(file.type)) return "Use a PNG, JPG, WebP or SVG image.";
  if (file.size > ICON_MAX_BYTES) return "That image is over 2 MB. Use a smaller one.";
  return null;
}

/** Where a w×h picture lands inside the square: fitted (never cropped), centred, never upscaled
 *  past the square. */
export function fitInSquare(w: number, h: number, size = ICON_SIZE): { x: number; y: number; w: number; h: number } {
  if (!(w > 0) || !(h > 0)) return { x: 0, y: 0, w: size, h: size };
  const scale = Math.min(size / w, size / h);
  const dw = w * scale, dh = h * scale;
  return { x: (size - dw) / 2, y: (size - dh) / 2, w: dw, h: dh };
}

/** A SHAPE, not a picture: at least 2% of the picture's OWN area is (nearly) transparent. `alpha` is
 *  the alpha (0–255) of the pixels INSIDE the fitted box only.
 *  🔴 NEVER THE WHOLE SQUARE (MEASURED 2026-09-23 by driving it): a wide opaque photo fitted into
 *  the square leaves empty bands above and below, and counting those called a photo a SHAPE — it
 *  would have been inked black. The bands are ours, not the coach's picture. */
export function looksLikeMask(alpha: ArrayLike<number>): boolean {
  if (alpha.length === 0) return false;
  let clear = 0;
  for (let i = 0; i < alpha.length; i++) if ((alpha[i] ?? 255) < 16) clear++;
  return clear / alpha.length >= 0.02;
}

/** The whole pixels a fitted box covers, shrunk by one on each side so an anti-aliased edge is not
 *  read as transparency. */
export function boxPixels(box: { x: number; y: number; w: number; h: number }, size = ICON_SIZE):
    { x0: number; y0: number; x1: number; y1: number } {
  const x0 = Math.min(size, Math.ceil(box.x) + 1), y0 = Math.min(size, Math.ceil(box.y) + 1);
  const x1 = Math.max(x0, Math.floor(box.x + box.w) - 1), y1 = Math.max(y0, Math.floor(box.y + box.h) - 1);
  return { x0, y0, x1, y1 };
}

/** Draw the file into the square and hand back the PNG and how it is to be drawn. */
export async function rasteriseIcon(file: File): Promise<{ png: Blob; isMask: boolean }> {
  const refusal = iconRefusal(file);
  if (refusal !== null) throw new Error(refusal);
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode().catch(() => { throw new Error("That image could not be read. Try another file."); });
    // An SVG with no size of its own reports 0×0 (or 150×150); fitting it to the square is right.
    const box = fitInSquare(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = ICON_SIZE; canvas.height = ICON_SIZE;
    const ctx = canvas.getContext("2d");
    if (ctx === null) throw new Error("This browser cannot draw the icon.");
    ctx.drawImage(img, box.x, box.y, box.w, box.h);
    const pixels = ctx.getImageData(0, 0, ICON_SIZE, ICON_SIZE);
    const r = boxPixels(box);
    const alpha: number[] = [];
    for (let y = r.y0; y < r.y1; y++) {
      for (let x = r.x0; x < r.x1; x++) alpha.push(pixels.data[(y * ICON_SIZE + x) * 4 + 3]!);
    }
    const isMask = looksLikeMask(alpha);
    if (isMask) {
      // Ink every pixel, keep the shape: the colour a coach's file happened to have is not his
      // icon's colour here — the page's ink is, in both themes.
      for (let i = 0; i < pixels.data.length; i += 4) { pixels.data[i] = 0; pixels.data[i + 1] = 0; pixels.data[i + 2] = 0; }
      ctx.putImageData(pixels, 0, 0);
    }
    const png = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b === null ? reject(new Error("The icon could not be made.")) : resolve(b)), "image/png"));
    return { png, isMask };
  } finally {
    URL.revokeObjectURL(url);
  }
}
